import { useCallback, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@repo/ui';

import { LoanRecipient } from '@/components/loan-form/loan-recipient/loan-recipient';
import { LoanAgreements } from '@/components/loan-form/loan-agreements/loan-agreements';
import {
  useMainHubStoreActions,
  useMainHubStoreAvailableResponseDataState,
} from '@/store/main-hub.store';
import { VIEW_TYPE } from '@/store/view.type';
import { commonLoanFormInitialData } from '@/components/common/helpers/loan-form-initial-data';
import {
  companyType,
  FormSchemaByCompanyType,
  schemaByCompanyType,
} from '@/components/loan-form/schema/loan-form.schema';
import { useModalStore } from '@/components/modal/modal.store';

export function PragmaFormModalContent() {
  const { t } = useTranslation();
  const currentView = VIEW_TYPE.SELECT_OFFER_VIEW;
  const [step, setStep] = useState<'recipient' | 'agreements'>('recipient');
  const { getProviderConfig } = useMainHubStoreActions();
  const { merchantResponseData } = useMainHubStoreAvailableResponseDataState(currentView);
  const { additionalProps, closeModal } = useModalStore();

  const providerConfig = getProviderConfig('pragma');

  const { data } = merchantResponseData;

  const defaultValues = commonLoanFormInitialData({
    data: data,
    provider: 'pragma',
  });

  const selectedCompanyType = companyType(data.merchantData.companyType);
  const selectedSchema = schemaByCompanyType[selectedCompanyType];

  type FormSchema = FormSchemaByCompanyType<typeof selectedCompanyType>;

  const fv = additionalProps.formValues;
  const initialAmount =
    typeof fv === 'object' && fv !== null && 'amount' in fv && typeof fv.amount === 'number'
      ? fv.amount
      : 10000;

  const methods = useForm<FormSchema>({
    defaultValues: {
      ...defaultValues,
      // AMOUNT AND PERIOD HARDCODED. DO NOT CHANGE!
      amount: initialAmount,
      period: '2',
    },
    resolver: zodResolver(selectedSchema),
  });

  const preSubmitCheck = () => {
    if (selectedCompanyType === 'jednoosobowa') {
      const documentType = methods.getValues().documentType;
      if (!documentType || !['idCard', 'polishPassport'].includes(documentType)) {
        methods.setError('documentId', {
          type: 'manual',
          message: t('loanFormPage.borrowerDetails.form.validation.documentType'),
        });

        return false;
      }
    }

    return true;
  };

  const marketingSubmitRef = useRef<(() => void) | null>(null);
  const registerMarketingSubmit = useCallback((submit: () => void) => {
    marketingSubmitRef.current = submit;
  }, []);

  const onSubmit = (values: FormSchema) => {
    if (additionalProps && typeof additionalProps.onSubmit === 'function') {
      // Fire the marketing-consent request before closeModal unmounts the form.
      marketingSubmitRef.current?.();
      additionalProps.onSubmit(values);
      closeModal();
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        data-testid="loan-form"
        onSubmit={(event) => {
          if (!preSubmitCheck()) {
            event.preventDefault();

            return;
          }
          void methods.handleSubmit(onSubmit)(event);
        }}
      >
        {step === 'recipient' && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {providerConfig && (
              <LoanRecipient providerConfig={providerConfig} viewType={'SELECT_OFFER_VIEW'} />
            )}
          </Box>
        )}
        {step === 'agreements' && (
          <>
            <Typography variant="h4" mb={2}>
              {t('selectOfferPage.multiprovider.modal.pragma.title')}
            </Typography>
            <Box mt={1}>
              <LoanAgreements
                merchantResponseData={merchantResponseData}
                onRegisterMarketingSubmit={registerMarketingSubmit}
              />
            </Box>
          </>
        )}
        <Box marginTop={2}>
          {step === 'recipient' ? (
            <Button
              color="info"
              data-testid="loan-form-view-next-btn"
              size="large"
              type="button"
              variant="contained"
              onClick={() => setStep('agreements')}
              sx={{
                display: 'block',
                margin: 'auto',
                fontSize: '15px',
                paddingY: { xs: '11px', sm: '19px' },
              }}
            >
              {t('selectOfferPage.multiprovider.modal.pragma.confirmButton')}
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                // color="info"
                data-testid="loan-form-view-back-btn"
                size="large"
                type="button"
                variant="text"
                onClick={() => setStep('recipient')}
                sx={{
                  fontSize: '15px',
                  paddingY: { xs: '11px', sm: '19px' },
                }}
              >
                {t('selectOfferPage.multiprovider.modal.pragma.backButton')}
              </Button>
              <Button
                color="info"
                data-testid="loan-form-view-submit-btn"
                size="large"
                type="submit"
                variant="contained"
                sx={{
                  fontSize: '15px',
                  paddingY: { xs: '11px', sm: '19px' },
                }}
              >
                {t('selectOfferPage.multiprovider.modal.pragma.submitButton')}
              </Button>
            </Box>
          )}
        </Box>
      </form>
    </FormProvider>
  );
}
